
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TeamsList } from '@/features/teams/components/TeamsList';
import { TeamMembers } from '@/features/teams/components/TeamMembers';
import { CreateTeamDialog } from '@/features/teams/components/CreateTeamDialog';
import { EditTeamDialog } from '@/features/teams/components/EditTeamDialog';
import { DeleteTeamDialog } from '@/features/teams/components/DeleteTeamDialog';
import { useTeams, Team } from '@/features/teams/hooks/useTeams';

export default function Teams() {
  const { teams, isLoading, error, createTeam, updateTeam, deleteTeam } = useTeams();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentTeam, setCurrentTeam] = useState<Team | null>(null);

  // Mocked team members data for demonstration
  const teamMembers = [
    {
      id: '1',
      name: 'Alex Johnson',
      email: 'alex.j@example.com',
      role: 'admin' as const,
      avatar: 'https://i.pravatar.cc/150?u=1',
    },
    {
      id: '2',
      name: 'Sam Lee',
      email: 'sam.lee@example.com',
      role: 'member' as const,
      avatar: 'https://i.pravatar.cc/150?u=2',
    },
  ];

  const handleCreateTeam = (name: string) => {
    createTeam.mutate(name, {
      onSuccess: () => setIsCreateDialogOpen(false)
    });
  };

  const handleUpdateTeam = (id: string, name: string) => {
    updateTeam.mutate({ id, name }, {
      onSuccess: () => setIsEditDialogOpen(false)
    });
  };

  const handleDeleteTeam = (id: string) => {
    deleteTeam.mutate(id, {
      onSuccess: () => setIsDeleteDialogOpen(false)
    });
  };

  const openEditDialog = (team: Team) => {
    setCurrentTeam(team);
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (team: Team) => {
    setCurrentTeam(team);
    setIsDeleteDialogOpen(true);
  };

  const handleInviteMember = () => {
    // This would open a dialog to invite a new member
    console.log('Invite member clicked');
  };

  return (
    <DashboardLayout>
      <motion.div
        className="space-y-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center justify-between">
          <motion.h1 
            className="text-3xl font-semibold"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            Teams
          </motion.h1>
          
          <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              New Team
            </Button>
          </motion.div>
        </div>
        
        <Tabs defaultValue="teams" className="mt-6">
          <TabsList>
            <TabsTrigger value="teams">My Teams</TabsTrigger>
            <TabsTrigger value="members">Team Members</TabsTrigger>
          </TabsList>
          
          <TabsContent value="teams" className="mt-4">
            <TeamsList 
              teams={teams}
              isLoading={isLoading}
              error={error as Error}
              onCreateTeam={() => setIsCreateDialogOpen(true)}
              onEditTeam={openEditDialog}
              onDeleteTeam={openDeleteDialog}
            />
          </TabsContent>
          
          <TabsContent value="members" className="mt-4">
            <TeamMembers 
              members={teamMembers}
              onInviteMember={handleInviteMember}
            />
          </TabsContent>
        </Tabs>
      </motion.div>

      {/* Dialogs */}
      <CreateTeamDialog 
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onCreateTeam={handleCreateTeam}
        isCreating={createTeam.isPending}
      />

      <EditTeamDialog 
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        onUpdateTeam={handleUpdateTeam}
        team={currentTeam}
        isUpdating={updateTeam.isPending}
      />

      <DeleteTeamDialog 
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onDeleteTeam={handleDeleteTeam}
        team={currentTeam}
        isDeleting={deleteTeam.isPending}
      />
    </DashboardLayout>
  );
}
